// src/pages/Dashboard.js
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "./Dashboard.css";
import WorkTimeStatisticsModal from "../components/WorkTimeStatisticsModal";
import CloseOrdersModal from "../components/CloseOrdersModal";
import AssignOrdersModal from "../components/AssignOrdersModal"; 
import InvoiceOrdersModal from "../components/InvoiceOrdersModal";
import { API_URL } from "../config";

/* ---------------- POMOCNICZE ---------------- */
const formatDate = (dateString) => {
  if (!dateString) return "-";
  const d = new Date(dateString);
  return `${String(d.getDate()).padStart(2, "0")}-${String(
    d.getMonth() + 1
  ).padStart(2, "0")}-${d.getFullYear()}`;
};

const PRIORITY_ORDER = ["Bardzo Niski", "Niski", "Neutralny", "Wysoki", "Krytyczny"];
const STATUS_ORDER   = [
  "Oczekujące",
  "Przydzielone",
  "Realizowane",
  "Wstrzymane",
  "Walidacja",
  "Zatwierdzone",
  "Fakturowanie",
  "Zamknięte",
];

/* ====================== KOMPONENT ====================== */
function Dashboard() {
  const navigate       = useNavigate();
  const currentUser    = JSON.parse(localStorage.getItem("user"));
  
  const isAdmin        = currentUser && currentUser.role === "Admin";

  const [assignedOrders, setAssignedOrders] = useState([]);
  const [pendingOrders,  setPendingOrders]  = useState([]);
  const [sortConfig,     setSortConfig]     = useState({ key: null, direction: "asc" });

  const [ordersToClose,  setOrdersToClose]  = useState([]);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [loading,        setLoading]        = useState(false);
  
  const [ordersToInvoice, setOrdersToInvoice] = useState([]);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [loadingInvoice, setLoadingInvoice] = useState(false);

  /* ------------- wyceny materiałów ------------- */
  const [materialCosts,  setMaterialCosts]  = useState({}); // { orderId: number }
  const [workHours,      setWorkHours]      = useState({}); // { orderId: number }

  /* ------------- lista klientów (Szef) ---------- */
  const [clients,        setClients]        = useState([]); // <<< NEW
  const [selectedClient, setSelectedClient] = useState("all"); // <<< NEW

  const isAdminOrBoss =
    currentUser && (currentUser.role === "Admin" || currentUser.position === "Szef");

  // >>> DODANE: widoczność dla Admin/Moderator + licznik i modal
  const isAdminOrModerator =
    currentUser && (currentUser.role === "Admin" || currentUser.role === "Moderator");

  const [assignableCount, setAssignableCount] = useState(0);
  const [loadingAssignable, setLoadingAssignable] = useState(false);
  const [showAssignOrdersModal, setShowAssignOrdersModal] = useState(false);
  // <<< DODANE

  /* ---------------- POBIERANIE ZLECEŃ ---------------- */
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const res  = await fetch(`${API_URL}/orders`, {
          credentials: "include",
        });
        if (!res.ok) throw new Error("Błąd pobierania zleceń");
        const data = await res.json();

        /* -------- KAFELKI -------- */
        const baseAssigned = data.filter(
          (o) =>
            (Number(o.osoba_wykonujaca_id) === Number(currentUser.id) &&
              o.status === "Realizowane") ||
            (Number(o.osoba_walidujaca_id) === Number(currentUser.id) &&
              o.status === "Walidacja")
        );

        // jeśli Szef – dorzuć wszystkie Zatwierdzone i Fakturowanie
        let finalAssigned = baseAssigned;
        if (currentUser.position === "Szef") {
		  const extra = data.filter((o) => ["Zatwierdzone", "Fakturowanie"].includes(o.status));
		  finalAssigned = [
			...baseAssigned,
			...extra.filter((x) => !baseAssigned.some((ba) => ba.id === x.id)),
		  ];
        }
        setAssignedOrders(finalAssigned);

        /* -------- LISTA -------- */
        setPendingOrders(
          data.filter(
            (o) =>
              Number(o.osoba_wykonujaca_id) === Number(currentUser.id) &&
              ["Oczekujące", "Przydzielone", "Wstrzymane"].includes(o.status)
          )
        );
      } catch (err) {
        console.error(err);
      }
    };

    if (currentUser) fetchOrders();
    else navigate("/login");
  }, [currentUser, navigate]);

  /* ---------- POBIERANIE LISTY KLIENTÓW (tylko Szef) ---------- */
  useEffect(() => {
    if (!currentUser || currentUser.position !== "Szef") return;           // <<< NEW
    const fetchClients = async () => {                                     // <<< NEW
      try {
        const res = await fetch(`${API_URL}/orders/clients`);
        if (!res.ok) throw new Error();
        setClients(await res.json());
      } catch (err) {
        console.error("Błąd pobierania listy klientów:", err);
      }
    };
    fetchClients();
  }, [currentUser]);                                                       // <<< NEW

  /* ---------- POBIERANIE WYCEŃ DLA ZATWIERDZONYCH ---------- */
  useEffect(() => {
    const approved = assignedOrders.filter((o) => o.status === "Zatwierdzone");

    if (approved.length === 0) return;
    const missing = approved.filter((o) => materialCosts[o.id] === undefined);
    if (missing.length === 0) return;

    const fetchCosts = async () => {
      const updates = {};
      await Promise.all(
        missing.map(async (o) => {
          try {
            const res = await fetch(
              `${API_URL}/materials/${o.id}`,
              { credentials: "include" }
            );
            if (!res.ok) throw new Error();
            const mats = await res.json();
            const sum  = mats.reduce(
              (acc, m) => acc + (m.price ? Number(m.price) : 0),
              0
            );
            updates[o.id] = sum;
          } catch {
            updates[o.id] = null;
          }
        })
      );
      setMaterialCosts((prev) => ({ ...prev, ...updates }));
    };

    fetchCosts();
  }, [assignedOrders, materialCosts]);

  /* ---------- POBIERANIE GODZIN DLA ZATWIERDZONYCH ---------- */
  useEffect(() => {
    const approved = assignedOrders.filter((o) => ["Zatwierdzone", "Fakturowanie"].includes(o.status));
    if (approved.length === 0) return;

    const missing = approved.filter((o) => workHours[o.id] === undefined);
    if (missing.length === 0) return;

    const fetchHours = async () => {
      const updates = {};
      await Promise.all(
        missing.map(async (o) => {
          try {
            const res = await fetch(
              `${API_URL}/orders/${o.id}/worktime`,
              { credentials: "include" }
            );
            if (!res.ok) throw new Error();
            const wt  = await res.json();            // { totalHours, summary, … }
            const sum = Number(wt.totalHours || 0);  // bierzemy gotową sumę
            updates[o.id] = sum;
          } catch {
            updates[o.id] = null;
          }
        })
      );
      setWorkHours((prev) => ({ ...prev, ...updates }));
    };

    fetchHours();
  }, [assignedOrders, workHours]);

  /* --------- ZAMKNIĘCIA (dla Admin/Szef) --------- */
  useEffect(() => {
    if (isAdminOrBoss) fetchOrdersToClose();
  }, [isAdminOrBoss]);
  
  /* --------- FAKTUROWANIE (tylko Admin) --------- */
  useEffect(() => {
    if (isAdmin) fetchOrdersToInvoice();
  }, [isAdmin]);

  const fetchOrdersToClose = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/orders/to-close`,
        { credentials: "include" }
      );
      if (!res.ok) throw new Error("Błąd pobierania zleceń");
      setOrdersToClose(await res.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchOrdersToInvoice = async () => {
    setLoadingInvoice(true);
    try {
      const res = await fetch(
        `${API_URL}/orders/to-close`,
        { credentials: "include" }
      );
      if (!res.ok) throw new Error("Błąd pobierania zleceń");
      const data = await res.json();
      setOrdersToInvoice(data.filter(o => o.status === "Fakturowanie"));
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingInvoice(false);
    }
  };

  const handleCloseOrder = async (orderId) => {
    try {
      const res = await fetch(
        `${API_URL}/orders/${orderId}/close`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ osoba_zamykajaca_id: currentUser.id }),
        }
      );
      if (!res.ok) throw new Error("Błąd zamykania zlecenia");
      fetchOrdersToClose();
	  fetchOrdersToInvoice();
    } catch (err) {
      console.error(err);
    }
  };

  /* --------- LICZNIK: zlecenia wymagające przydzielenia (Admin/Moderator) --------- */
  useEffect(() => {
    if (isAdminOrModerator) fetchAssignableCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminOrModerator]);

  const fetchAssignableCount = async () => {
    setLoadingAssignable(true);
    try {
      const res = await fetch(`${API_URL}/orders`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Błąd pobierania zleceń");
      const data = await res.json();
      const count = (data || []).filter(o => {
        const execId = o.osoba_wykonujaca_id == null ? null : Number(o.osoba_wykonujaca_id);
        return execId === null || execId === 29;
      }).length;
      setAssignableCount(count);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingAssignable(false);
    }
  };

  /* ---------- FILTR KLIENTA (Szef) ---------- */                                 // <<< NEW
  const visibleAssigned = useMemo(() => {                                        // <<< NEW
    if (selectedClient === "all") return assignedOrders;
    return assignedOrders.filter(
      (o) => String(o.klient_id) === String(selectedClient)
    );
  }, [assignedOrders, selectedClient]);                                          // <<< NEW

  /* --------- SORTOWANIE LISTY OCZEKUJĄCYCH --------- */
  const sortedPending = useMemo(() => {
    if (!sortConfig.key) return pendingOrders;
    return [...pendingOrders].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      if (sortConfig.key === "waga") {
        aVal = PRIORITY_ORDER.indexOf(aVal);
        bVal = PRIORITY_ORDER.indexOf(bVal);
      } else if (sortConfig.key === "status") {
        aVal = STATUS_ORDER.indexOf(aVal);
        bVal = STATUS_ORDER.indexOf(bVal);
      } else if (["data_zlecenia", "deadline"].includes(sortConfig.key)) {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      } else if (sortConfig.key === "zgłaszający") {
        aVal = `${a.last_name} ${a.first_name}`.toLowerCase();
        bVal = `${b.last_name} ${b.first_name}`.toLowerCase();
      } else {
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
      }

      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [pendingOrders, sortConfig]);

  const handleSort = (key) =>
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );

  /* --------- MODAL STATYSTYK --------- */
  let statsOptions = [];
  if (
    currentUser &&
    ["Szef", "Manager", "Project Manager", "Logistyk"].includes(currentUser.position)
  ) {
    statsOptions = ["Mój Czas Pracy", "Czas Pracy Pracownika", "Czas Pracy Zlecenia"];
  } else if (currentUser) {
    statsOptions = ["Mój Czas Pracy"];
  }

  const [selectedOption, setSelectedOption] = useState("");
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [statsData, setStatsData] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [statsError, setStatsError] = useState("");

  const handleOptionClick = (opt) => {
    setSelectedOption(opt);
    if (opt === "Mój Czas Pracy") fetchMyTimeStats();
    else setStatsData(null);
    setShowStatsModal(true);
  };

  const fetchMyTimeStats = async () => {
    setLoadingStats(true);
    setStatsError("");
    try {
      const res = await fetch(
        `${API_URL}/working_time/statistics/my`,
        { credentials: "include" }
      );
      if (!res.ok) throw new Error("Błąd pobierania statystyk.");
      setStatsData(await res.json());
    } catch (err) {
      setStatsError(err.message);
    } finally {
      setLoadingStats(false);
    }
  };

  /* --------- REDIRECT, JEŚLI BRAK USERA --------- */
  if (!currentUser) {
    return (
      <div>
        Nie zalogowany. Przekierowanie do logowania...
        {setTimeout(() => navigate("/login"), 2000)}
      </div>
    );
  }

  /* ================= RENDER ================= */
  return (
    <div className="dashboard-container">
      <div className="dashboard-main" style={{ position: "relative", width: "80%" }}>
        <h1 className="dashboard-title">Dashboard</h1>

        {isAdminOrBoss && (
          <button
            className="close-orders-btn"
            onClick={() => setShowCloseModal(true)}
            style={{ position: "absolute", top: "1rem", right: "1rem" }}
          >
            Zlecenia do Zamknięcia ({ordersToClose.length})
          </button>
        )}

        {/* DODANE: przycisk przydzielania – Admin i Moderator */}
        {isAdminOrModerator && (
          <button
            className="close-orders-btn"
            onClick={() => setShowAssignOrdersModal(true)}
            disabled={loadingAssignable}
            style={{
              position: "absolute",
              top: "1rem",
              right: "1rem",
              ...(currentUser.role === "Admin"
                ? { transform: "translateX(-206%)", marginRight: "0.5rem" }
                : {}),
            }}
          >
            Zlecenia do Przydzielenia ({assignableCount})
          </button>
        )}
		
		{isAdmin && (
		  <button
			className="close-orders-btn"
			onClick={() => setShowInvoiceModal(true)}
			disabled={loadingInvoice}
			style={{
			  position: "absolute",
			  top: "1rem",
			  right: "1rem",
			  transform: "translateX(-100%)",
			  marginRight: "0.5rem",
			}}
		  >
			Zlecenia do Fakturowania ({ordersToInvoice.length})
		  </button>
		)}

		{/* -------- SELECT KLIENTA (tylko Szef) -------- */}
		{currentUser.position === "Szef" && (
		  <select
		    className="client-filter-select"
			value={selectedClient}
			onChange={(e) => setSelectedClient(e.target.value)}
			style={{ position: "absolute", top: "4.5rem", right: "1rem", padding: "0.4rem" }}
		  >
			<option value="all">Wszyscy klienci</option>
			{clients.map((c) => (
			  <option key={c.id} value={c.id}>
				{c.name}
			  </option>
			))}
		  </select>
		)}

        <h2 className="assigned-section-title">Zlecenia Realizowane przez Ciebie:</h2>

        <div className="orders-grid">
          {visibleAssigned.length === 0 ? (                                    
            <p>Brak zleceń do wyświetlenia.</p>
          ) : (
            visibleAssigned.map((o) => {                                      
              const isApproved  = o.status === "Zatwierdzone";
			  const isInvoicing = o.status === "Fakturowanie";
              const cost       = materialCosts[o.id];
              const hours      = workHours[o.id];
              return (
                <div
                  key={o.id}
                  className={`order-card ${
                    o.status === "Walidacja"
                      ? "validation"
					  : isApproved ? "approved"
					  : isInvoicing ? "invoicing" : ""
                  }`}
                  onClick={() => navigate(`/zlecenia/${o.id}`)}
                >
                  {o.status === "Walidacja" && (
                    <p className="validation-label">WALIDACJA</p>
                  )}
                  {isApproved && <p className="approved-label">DO&nbsp;ZAMKNIĘCIA</p>}
				  {isInvoicing && <p className="invoicing-label">FAKTUROWANIE</p>}

                  <p>
                    <strong>NR Zlecenia:</strong> {o.nr_ref}
                  </p>
                  <p>
                    <strong>Tytuł:</strong> {o.tytul}
                  </p>
                  <p>
                    <strong>Termin:</strong> {formatDate(o.deadline)}
                  </p>

                  {(isApproved || isInvoicing) ? (
                    <>
                      <p>
                        <strong>Klient:</strong>{" "}
                        {o.client_name ? o.client_name : "-"}
                      </p>
                      <p>
                        <strong>Godziny:</strong>{" "}
                        {hours === undefined
                          ? "..."
                          : hours === null
                          ? "-"
                          : Number.isInteger(hours) ? hours : hours.toFixed(1)}
                      </p>
                      <p>
                        <strong>Materiał:</strong>{" "}
                        {cost === undefined
                          ? "..."
                          : cost === null
                          ? "-"
                          : `${cost.toFixed(2)} zł`}
                      </p>
                    </>
                  ) : (
                    <>
                      <p>
                        <strong>Zgłaszający:</strong> {o.first_name} {o.last_name}
                      </p>
                      <p>
                        <strong>Priorytet:</strong> {o.waga}
                      </p>
                    </>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* -------- LISTA OCZEKUJĄCYCH -------- */}
        <h2 className="pending-section-title">Zlecenia Oczekujące na Twoje działanie:</h2>
        <div className="pending-list">
          <table>
            <thead>
              <tr>
                {[
                  { key: "nr_ref", label: "NR Zlecenia" },
                  { key: "tytul", label: "Tytuł" },
                  { key: "data_zlecenia", label: "Data Zgłoszenia" },
                  { key: "deadline", label: "Termin" },
                  { key: "zgłaszający", label: "Zgłaszający" },
                  { key: "waga", label: "Priorytet" },
                  { key: "status", label: "Status" },
                ].map(({ key, label }) => (
                  <th key={key} onClick={() => handleSort(key)}>
                    {label}{" "}
                    {sortConfig.key === key
                      ? sortConfig.direction === "asc"
                        ? "▲"
                        : "▼"
                      : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedPending.map((o) => (
                <tr key={o.id} onClick={() => navigate(`/zlecenia/${o.id}`)}>
                  <td className="wrap">{o.nr_ref}</td>
                  <td className="wrap">{o.tytul}</td>
                  <td>{formatDate(o.data_zlecenia)}</td>
                  <td>{formatDate(o.deadline)}</td>
                  <td className="wrap">
                    {o.first_name} {o.last_name}
                  </td>
                  <td>{o.waga}</td>
                  <td>{o.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* -------- PANEL STATYSTYK -------- */}
      <div className="dashboard-stats">
        <h2>Statystyki Czasu Pracy</h2>
		<div className="stats-menu">
		  {statsOptions.map((opt) => (
			<button
			  key={opt}
			  onClick={() => handleOptionClick(opt)}
			  className={selectedOption === opt ? "active" : ""}
			>
			  {opt}
			</button>
		  ))}

		  {/* jeden, zawsze pojedynczy przycisk kalendarza */}
		  <button
			className="stats-menu-btn"
			style={{ marginTop: "1rem" }}
			onClick={() => navigate("/kalendarz")}
		  >
			Kalendarz
		  </button>
		</div>

        {showStatsModal && (
          <WorkTimeStatisticsModal
            onClose={() => setShowStatsModal(false)}
            option={selectedOption}
            statsData={statsData}
            loadingStats={loadingStats}
            statsError={statsError}
          />
        )}
      </div>

      {showCloseModal && (
        <CloseOrdersModal
          orders={ordersToClose}
          onClose={() => setShowCloseModal(false)}
          onCloseOrder={handleCloseOrder}
          onViewOrder={(orderId) => navigate(`/zlecenia/${orderId}`)}
        />
      )}
	  
	  {showInvoiceModal && (
	    <InvoiceOrdersModal
		  orders={ordersToInvoice}
		  onClose={() => setShowInvoiceModal(false)}
		  onCloseOrder={handleCloseOrder}
		  onViewOrder={(id) => navigate(`/zlecenia/${id}`)}
	    />
	  )}

      {/* DODANE: modal przydzielania */}
      {showAssignOrdersModal && (
        <AssignOrdersModal
          visible
          onClose={() => {
            setShowAssignOrdersModal(false);
            fetchAssignableCount(); // odśwież licznik po ewentualnych zmianach
          }}
        />
      )}
    </div>
  );
}

export default Dashboard;
